import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import { Formik, Field } from 'formik';
import { getAvailChannels } from 'store/actions/avail';
import constants from 'constants/index';

import {
  ToggleContainer,
  MeshControl,
  Slider,
  MeshCheckbox,
} from './RPCFormStyles';
import {
  FovModal,
  FovToggle,
  FovButton,
  FovSelect,
  FovOption,
  VerticalSpacer,
  IndeterminateProgress,
  FovUploader,
  CreditsOutlined,
  DocNote,
  HorizontalSpacer,
  Body,
  Checkbox,
  ShareOutlined,
} from 'components';
import { withTheme } from 'styled-components';
import _ from 'lodash';
import * as yup from 'yup';
import { SUPPORTED_EXTENSIONS, DOWNSCALE_SIZES } from 'utils';
import {
  AddCreditsContainer,
  BalanceContainer,
  CreditBalance,
  TitleWithTooltipContainer,
} from '../Publish/publishStyles';
import { Proxies } from 'store/actions/rpc';
import { IconTitlePair } from '../ModelToolbarStyles';
import NameField from './NameField';
import CategorySelect from './CategorySelect';
import PublishFormatsField, { PUBLISH_FORMATS, DEFAULT_PUBLISH_FORMATS } from './PublishFormatsField';
import TagsField from './TagsField';
import HeightFields from './HeightFields';
import AIFields from './AIFields';
import AIModelSelect from './AIModelSelect';
import OptimizationSection from './OptimizationSection';
import ProxySection from './ProxySection';
import DebugToggleSection from './DebugToggleSection';

const units = [
  { label: 'in', factor: 1 },
  { label: 'ft', factor: 12 },
  { label: 'm', factor: 39.37 },
  { label: 'cm', factor: 0.393701 },
];

const AIModels = [
  { name: 'Tripo', supportMultiImage: false },
  { name: 'Meshy', supportMultiImage: true },
];

const IMAGE_EXTENSIONS = [ 'png', 'jpg', 'jpeg' ];

const proxyOptions = [
  Proxies.billboard,
  Proxies.simpleMesh,
  Proxies.boundingBox,
];

const downscaleOptions = DOWNSCALE_SIZES?.map((option, index) => (
  <FovOption key={index} value={option.value}>
    {option.name}
  </FovOption>
));


const buildCategoriesOptions = rpcCategories => {
  if (!_.isEmpty(rpcCategories)) {
    return rpcCategories.map(category => {
      const hasChildren = category.children?.length > 0;
      const childrenArray = hasChildren && [ ...category.children, category ];
      const arrayWithNested = hasChildren ? childrenArray.reverse() : [ category ];

      return arrayWithNested.map((item, index) => {
        const { title, value, children } = item;

        return (
          <FovOption
            key={index}
            value={value}
            indent={!children}
            groupParent={children}
          >
            {title}
          </FovOption>
        );
      });
    });
  }

  return (
    <FovOption value='No categories found.'>
      No categories found.
    </FovOption>
  );
};

const RPCForm = props => {
  const {
    isVisible,
    setIsVisible,
    onSubmit,
    isModelForSaving,
    setAvailLoggedIn,
    theme,
  } = props;

  const [ isImageProvided, setIsImageProvided ] = useState(false);
  const [ compactRPCEnabled, setCompactRPCEnabled ] = useState(false);
  const [ useModelOptimization, setUseModelOptimization ] = useState(false);
  const [ useMeshOptimization, setUseMeshOptimization ] = useState(false);
  const [ useAI, setUseAI ] = useState(false);

  const [ useTextureOptimization, setUseTextureOptimization ] = useState(false);
  const [ optimizationAmount, setOptimizationAmount ] = useState(0.99);
  const [ estimatedFacesCount, setEstimatedFacesCount ] = useState(0);
  const [ debugState, setDebugState ] = useState(false);
  const [ enableMeshSlider, setEnableMeshSlider ] = useState(false);
  const [ enableMeshOptimizationSlider, setEnableMeshOptimizationSlider ] = useState(false);
  const [ selectedProxyMesh, setSelectedProxyMesh ] = useState(Proxies.billboard);
  const [ cookies ] = useCookies([ 'userAuth' ]);

  const dispatch = useDispatch();
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const rpcStore = useSelector(state => state.rpc);
  const darkGrey = theme.colors[selectedTheme].darkGrey;
  const rpcCategories = rpcStore.rpcCategories;
  const token = cookies.archvision_token;
  const creditBalance = userDataStore?.data?.user_points || 0;

  const defaults = {
    contentName: '',
    categoryName: 'Other',
    description: '',
    height: '',
    heightFactor: `${units[0].factor}`,
    files: '',
    proxyName: Proxies.billboard,
    ai: false,
    aiModel: '',
    formats: [ ...DEFAULT_PUBLISH_FORMATS ],
  };

  const tooltipColor = selectedTheme === constants.THEME.LIGHT
    ? theme.colors[selectedTheme].darkGrey
    : theme.colors[selectedTheme].lightGrey;

  const schema = yup.object().shape({
    contentName: yup.string().required('Name is required.'),
    categoryName: yup.string().required('Category is required.'),
    description: yup.string().required('At least one tag is required.'),
    files: yup.mixed().required('A file is required.'),
    formats: yup.array()
      .of(yup.string())
      .min(1, 'At least one format is required.'),
    height: yup.number().when('isImage', {
      is: () => isImageProvided === true && !useAI,
      then: yup.number()
        .typeError('Must be numeric value.')
        .required('Height is required.')
        .positive('Must be positive number.'),
      otherwise: yup.number().optional(),
    }),
  });

  const categories = useMemo(() => {
    return buildCategoriesOptions(rpcCategories);
  }, [ rpcCategories ]);

  const cleanup = () => {
    setIsImageProvided(false);
    setCompactRPCEnabled(false);
    setUseModelOptimization(false);
    setOptimizationAmount(0.99);
    setEstimatedFacesCount(0);
    setDebugState(false);
    setUseTextureOptimization(false);
    setSelectedProxyMesh(Proxies.billboard);
    setEnableMeshSlider(false);
    setEnableMeshOptimizationSlider(false);
    setUseMeshOptimization(false);
    setUseAI(false);
  };

  const handleClose = () => {
    if (isVisible) {
      setIsVisible(false);
      cleanup();
    }
  };

  const handleCompactRPCChange = value => {
    setCompactRPCEnabled(value);

    if (value) {
      setUseModelOptimization(false);
    }
  };

  const handleMeshOptimizationChange = value => {
    setUseModelOptimization(value);

    if (value) {
      setCompactRPCEnabled(false);
    }
  };

  const handleSubmit = args => {
    const { values, setSubmitting } = args;
    const {
      height,
      heightFactor,
      files,
      optimizationAmount,
      ...baseValues
    } = values;


    const onComplete = () => {
      setSubmitting(false);
      handleClose();
    };

    let payload = { ...baseValues };

    const withHeight = {
      height: +(height * heightFactor),
      heightFactor,
    };


    const withFiles = { files };
    const withOptimization = { optimizationAmount };
    const withoutFiles = {
      optimizedMesh: compactRPCEnabled,
      useModelOptimization,
    };

    if (isImageProvided && !useAI) {
      payload = { ...payload, ...withHeight };
    }

    if (isModelForSaving) {
      payload = { ...payload, ...withFiles };
      return onSubmit(payload, debugState, onComplete);
    }

    if (useModelOptimization) {
      payload = { ...payload, ...withoutFiles, ...withOptimization };
      return onSubmit(payload, debugState, onComplete);
    }

    payload = { ...payload, ...withoutFiles };
    onSubmit(payload, debugState, onComplete);
  };



  useEffect(() => {
    dispatch(getAvailChannels(token, setAvailLoggedIn));
  }, []);

  return (
    <FovModal
      title={
        <IconTitlePair>
          {<ShareOutlined
            hoverColor={(theme, selectedTheme)}
            theme={theme}
            $selectedTheme={selectedTheme}
          />} <HorizontalSpacer size={10} />
          Publish | &nbsp;
          <Link
            to="/documentation/create-from-file"
            rel="noreferrer"
            target="_blank"
          >
            FOVEA upload guidelines
          </Link>
        </IconTitlePair>
      }
      visible={isVisible}
      onClose={handleClose}
    >
      <CreditBalance>
        <BalanceContainer>
          <CreditsOutlined width={18} height={18} /> <HorizontalSpacer size={10} />
          {creditBalance?.toLocaleString('en-US', { maximumFractionDigits: 2 })} Credits remaining.
          <VerticalSpacer size={10} />
        </BalanceContainer>
        <AddCreditsContainer>
          <strong>
            <Link to='//archvision.com/product/credits/' rel='noreferrer' target='_blank'>
              Add more credits...
            </Link>
          </strong>
          <DocNote path={[ 'publishPanel', 'addCredits' ]} top={-2} left={157} />
        </AddCreditsContainer>
        <VerticalSpacer size={15} />
      </CreditBalance>
      <Formik
        initialValues={defaults}
        validationSchema={schema}
        enableReinitialization={true}
        onSubmit={(values, handlers) => {
          const { setSubmitting } = handlers;
          handleSubmit({ values, setSubmitting });
        }}
      >
        {form => {
          const files = form.values?.files;
          const fileList = Array.isArray(files) ? files : (files ? [ files ] : []);
          const allFilesAreImages = fileList.length > 0 && fileList.every(file => {
            const ext = file?.name?.split('.').pop()?.toLowerCase();
            return ext && IMAGE_EXTENSIONS.includes(ext);
          });

          return (
          <Fragment>
            {isModelForSaving ? (
              <FovUploader
                form={form}
                formDefaults={defaults}
                updateNameField={true}
                title='File Upload*'
                instructions='Click or drag files here.'
                supportedExtensions={SUPPORTED_EXTENSIONS}
                setIsImageProvided={setIsImageProvided}
              />
            ) : (
              <OptimizationSection
                compactRPCEnabled={compactRPCEnabled}
                useModelOptimization={useModelOptimization}
                optimizationAmount={optimizationAmount}
                setOptimizationAmount={setOptimizationAmount}
                estimatedFacesCount={estimatedFacesCount}
                onCompactRPCChange={handleCompactRPCChange}
                onModelOptimizationChange={handleMeshOptimizationChange}
              />
            )}

            {allFilesAreImages && (
              <>
                <AIFields
                  form={form}
                  isImageProvided={true}
                  useAI={useAI}
                  setUseAI={setUseAI}
                  multipleFiles={fileList.length > 1}
                />
                <AIModelSelect
                  form={form}
                  isImageProvided={true}
                  useAI={useAI}
                  AIModels={AIModels}
                />
              </>
            )}
            {allFilesAreImages && fileList.length === 1 &&
              <HeightFields
                form={form}
                isImageProvided={isImageProvided}
                useAI={useAI}
                units={units}
              />
            }
            <NameField />
            <CategorySelect
              form={form}
              categories={categories}
              rpcCategories={rpcCategories}
            />
            <TagsField
              form={form}
              theme={theme}
              selectedTheme={selectedTheme}
            />
            <PublishFormatsField
              form={form}
              options={PUBLISH_FORMATS}
            />
            <ProxySection
              form={form}
              proxyOptions={proxyOptions}
              selectedProxyMesh={selectedProxyMesh}
              setSelectedProxyMesh={setSelectedProxyMesh}
              enableMeshSlider={enableMeshSlider}
              setEnableMeshSlider={setEnableMeshSlider}
            />
            <DebugToggleSection
              debugState={debugState}
              setDebugState={setDebugState}
            />

            {creditBalance === 0 && (
              <strong>
                <Link
                  to="//archvision.com/product/credits/"
                  rel="noreferrer"
                  target="_blank"
                >
                  Add more credits...
                </Link>
              </strong>
            )}

            <Body size='Medium Bold'>
              Optimization
              <DocNote path={[ 'publishPanel', 'globalOptimization' ]} top={-2} left={105} />
            </Body>

            <ToggleContainer>
              <TitleWithTooltipContainer>
                Texture downscaling
                <Body size="X-Small" color={tooltipColor}>
                  Affects textures larger than selected size.
                </Body>
              </TitleWithTooltipContainer>

              <Field type='checkbox' name='useTextureSimplification'>
                {data => (
                  <FovToggle
                    checked={!!(data.field.value)}
                    disabled={false}
                    callback={newVal => {
                      form.setFieldValue(data.field.name, newVal);
                      setUseTextureOptimization(newVal);
                    }}
                  />
                )}
              </Field>
            </ToggleContainer>
            <VerticalSpacer size={10} />
            {useTextureOptimization && (
              <>
                <VerticalSpacer size={10} />
                <Field type="select" name="downscaleSize">
                  {data => (
                    <FovSelect
                      value={data.field.value}
                      placeholder={
                       DOWNSCALE_SIZES.find(option => option.value === data.field.value)?.name || 'Select a resolution'}
                      onChange={(e, newVal) => {
                        form.setFieldValue(data.field.name, newVal);
                      }}
                    >
                      {downscaleOptions}
                    </FovSelect>
                  )}
                </Field>

                <VerticalSpacer size={15} />
              </>
            )}

            <ToggleContainer>
              <TitleWithTooltipContainer>
                Mesh simplification
              </TitleWithTooltipContainer>

              <Field type='checkbox' name='useModelOptimization'>
                {data => (
                  <FovToggle
                    checked={!!(data.field.value)}
                    disabled={false}
                    callback={newVal => {
                      form.setFieldValue(data.field.name, newVal);
                      setUseMeshOptimization(newVal);
                    }}
                  />
                )}
              </Field>
            </ToggleContainer>
            <VerticalSpacer size={15} />
            { useMeshOptimization && (
              <MeshControl>
                <Slider
                  defaultValue={0.5}
                  disabled={!enableMeshOptimizationSlider}
                  onAfterChange={newVal => {
                    form.setFieldValue('optimizationAmount', newVal);
                  }}
                />

                <MeshCheckbox htmlFor='meshCheckbox'>
                  <Checkbox
                    name='meshCheckbox'
                    checked={enableMeshOptimizationSlider}
                    onChange={e => {
                      const newVal = e.target.checked;
                      setEnableMeshOptimizationSlider(newVal);
                    }}
                  />
                  <HorizontalSpacer size={10} />
                  Use Custom Optimization
                </MeshCheckbox>
                <VerticalSpacer size={15} />
              </MeshControl>
            )}

            <FovButton
              type='submit'
              disabled={creditBalance === 0 || form.isSubmitting}
              onClick={form.handleSubmit}
            >
              {form.isSubmitting
                ? <IndeterminateProgress $color={darkGrey} />
                : 'Create'
              }
            </FovButton>
          </Fragment>
          );
        }}
      </Formik>
    </FovModal>
  );
};

export default withTheme(RPCForm);
